import type { Guest } from '../types/Guests';

export interface DuplicateGroup {
  key: string;
  guests: Guest[];
}

/** Same normalization used server-side for comparison — case/whitespace only. */
export const normalizeName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

/** Exact/near-exact (case + whitespace insensitive) name match — no fuzzy/nickname matching.
 *  Exported so callers (e.g. GuestManager's toolbar badge) share the same detection logic. */
export function findDuplicateGroups(guests: Guest[]): DuplicateGroup[] {
  const byName = new Map<string, Guest[]>();
  for (const guest of guests) {
    if (guest.archived) continue;
    const key = normalizeName(guest.name || '');
    if (!key) continue;
    const list = byName.get(key) ?? [];
    list.push(guest);
    byName.set(key, list);
  }
  return Array.from(byName.entries())
    .filter(([, list]) => list.length > 1)
    .map(([key, list]) => ({ key, guests: list }));
}

/** Client-side preview only — mirrors the server's actual merge rule (db/repositories/planning/guests.ts
 *  mergeGuests): keeper's own non-empty scalar wins, otherwise first donor's non-empty value; tags/
 *  dietary_restrictions are unioned. The server is the source of truth; this is display-only. */
export function previewMergedFields(keeper: Guest, losers: Guest[]): { field: string; value: string }[] {
  const scalarFields: Array<[string, keyof Guest]> = [
    ['Email', 'email'], ['Phone', 'phone'], ['RSVP status', 'rsvp_status'], ['Notes', 'notes'], ['Side', 'side'],
  ];
  const rows: { field: string; value: string }[] = [];
  for (const [label, field] of scalarFields) {
    const keeperVal = keeper[field];
    const winner = (keeperVal && String(keeperVal).length > 0)
      ? String(keeperVal)
      : String(losers.map((l) => l[field]).find((v) => v && String(v).length > 0) ?? '—');
    rows.push({ field: label, value: winner || '—' });
  }
  const tagLabels = new Set<string>();
  [keeper, ...losers].forEach((g) => g.tags.forEach((t) => tagLabels.add(t.label)));
  rows.push({ field: 'Tags (union)', value: tagLabels.size > 0 ? Array.from(tagLabels).join(', ') : '—' });
  return rows;
}
